/** The API's error envelope, as a typed exception. */

export type ApiErrorCode =
  | "validation_failed"
  | "bad_request"
  | "invalid_period"
  | "unauthenticated"
  | "token_expired"
  | "token_invalid"
  | "invalid_credentials"
  | "email_not_verified"
  | "forbidden"
  | "insufficient_plan"
  | "insufficient_role"
  | "not_business_member"
  | "turnstile_failed"
  | "not_found"
  | "already_exists"
  | "email_taken"
  | "conflict"
  | "invite_expired"
  | "invite_used"
  | "payload_too_large"
  | "rate_limited"
  | "ai_quota_exceeded"
  | "internal_error"
  | "upstream_error"
  | "not_configured"
  // Client-side sentinels — never returned by the server.
  | "network_error"
  | "aborted"
  | "unknown";

export interface ApiErrorEnvelope {
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
}

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly details: unknown;
  readonly requestId: string | null;
  readonly retryAfterSeconds: number | null;

  constructor(init: {
    code: string;
    message: string;
    status: number;
    details?: unknown;
    requestId?: string | null;
    retryAfterSeconds?: number | null;
  }) {
    super(init.message);
    this.name = "ApiError";
    this.code = init.code as ApiErrorCode;
    this.status = init.status;
    this.details = init.details ?? null;
    this.requestId = init.requestId ?? null;
    this.retryAfterSeconds = init.retryAfterSeconds ?? null;
  }

  /** `details` for insufficient_plan / plan-gated failures. */
  get planDetails(): { requiredPlan?: string; currentPlan?: string } {
    return isRecord(this.details) ? (this.details as { requiredPlan?: string; currentPlan?: string }) : {};
  }

  is(...codes: ApiErrorCode[]) {
    return codes.includes(this.code);
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

export function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/**
 * Flatten a Zod error tree from `validation_failed` into `{ field: message }`.
 * Handles both the modern `{ properties: { field: { errors: [...] } } }` tree and
 * the older `{ fieldErrors: { field: [...] } }` flattened shape.
 */
export function fieldErrorsFrom(details: unknown, prefix = ""): Record<string, string> {
  const out: Record<string, string> = {};
  if (!isRecord(details)) return out;

  const fieldErrors = details.fieldErrors;
  if (isRecord(fieldErrors)) {
    for (const [key, value] of Object.entries(fieldErrors)) {
      const msg = Array.isArray(value) ? value.find((v) => typeof v === "string") : value;
      if (typeof msg === "string") out[key] = msg;
    }
    return out;
  }

  const properties = details.properties;
  if (isRecord(properties)) {
    for (const [key, node] of Object.entries(properties)) {
      const path = prefix ? `${prefix}.${key}` : key;
      if (!isRecord(node)) continue;
      const errors = node.errors;
      if (Array.isArray(errors) && typeof errors[0] === "string") out[path] = errors[0];
      Object.assign(out, fieldErrorsFrom(node, path));
    }
    return out;
  }

  // A bare issues array: [{ path: ["email"], message: "..." }]
  const issues = Array.isArray(details.issues) ? details.issues : null;
  if (issues) {
    for (const issue of issues) {
      if (!isRecord(issue)) continue;
      const path = Array.isArray(issue.path) ? issue.path.join(".") : "";
      if (path && typeof issue.message === "string" && !out[path]) out[path] = issue.message;
    }
  }
  return out;
}

/** Human-facing copy for the codes where our wording beats the server's. */
const FRIENDLY: Partial<Record<ApiErrorCode, string>> = {
  network_error: "Can't reach Rocketeer Orbit. Check your connection and try again.",
  invalid_credentials: "That email and password combination doesn't match an account.",
  token_invalid: "Your session has ended. Please sign in again.",
  turnstile_failed: "The security check didn't pass. Please try again.",
  rate_limited: "Too many attempts. Please wait a moment and try again.",
  internal_error: "Something went wrong on our side. Please try again.",
  upstream_error: "A third-party service didn't respond. Please try again shortly.",
  not_configured: "This feature isn't configured yet.",
  aborted: "The request was cancelled.",
};

export function errorMessage(e: unknown, fallback = "Something went wrong."): string {
  if (isApiError(e)) return FRIENDLY[e.code] ?? e.message ?? fallback;
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}
